import _ from 'lodash';

import {ConversationTeamStatusesEnum} from '../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {isTeamNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {
  findConversationReminder,
  isConversationInTeamContext,
} from '../../../../src/helpers/models/conversationHelpers';
import {DismissedAlertsEnum} from '../../../../src/models/api/preferencesModel';
import {ConversationBasesSelector} from '../../../../src/store/data/conversations/conversationsSelectors';
import {getDismissedAlerts} from '../../../../src/store/data/preferences/preferencesSelectors';
import {CellsContextSelector} from '../../../../src/store/panels/viewer/conversations/viewerPanelConversationsSelectors';
import {createSelector} from '../../../helpers/reselectHelpers';
import {TriageActionsEnum} from './triageActionHelpers';

/*
 * Selectors.
 */

export function makeGetWillShowTriageForTeamAlert<P>(
  getCellsContext: CellsContextSelector<P>,
  getCurrentConversations: ConversationBasesSelector<P>,
  triageAction: TriageActionsEnum,
) {
  return createSelector(
    [getCellsContext, getCurrentConversations, getDismissedAlerts],
    (cellsContext, conversations, dismissedAlerts) => {
      const areAssignedToSomeoneElse = conversations.some(
        (c) => Boolean(c.assigneeId) && c.assigneeId !== cellsContext.teammateId,
      );
      const dismissedAlert = DismissedAlertsEnum.TRIAGE_TEAM;

      // If the user dismissed the alert AND conversation is NOT assigned to someone else, nothing to show.
      if (dismissedAlerts.includes(dismissedAlert) && !areAssignedToSomeoneElse) {
        return false;
      }

      const inboxesCount = _(conversations)
        .map((c) => c.inboxIds)
        .flatten()
        .uniq()
        .size();

      if (inboxesCount < 1) {
        return false;
      }

      const areInTeamContext = conversations.some((c) => isConversationInTeamContext(cellsContext, c));
      const areTeamConversations = conversations.some((c) => isTeamNamespace(c.namespace));
      const areAssignedToUser = conversations.some((c) => c.assigneeId === cellsContext.teammateId);
      const areTeamTriaged = conversations.some((c) => {
        switch (triageAction) {
          case TriageActionsEnum.ARCHIVE:
            return c.teamStatus === ConversationTeamStatusesEnum.ARCHIVED;
          case TriageActionsEnum.TRASH:
            return c.teamStatus === ConversationTeamStatusesEnum.TRASHED;
          case TriageActionsEnum.SPAM:
            return c.teamStatus === ConversationTeamStatusesEnum.SPAM;
          case TriageActionsEnum.SNOOZE:
            return Boolean(findConversationReminder(cellsContext, c));

          default:
            return true;
        }
      });

      if (areTeamTriaged) {
        return false;
      }

      return areInTeamContext || (areTeamConversations && areAssignedToUser);
    },
  );
}
